/**
 * CreateDocumentLinkDialog — modal pour lier un fichier à une entité (Chunk 9.L.13).
 *
 * Backend : `POST /v1/document-links` (9.J) avec gating Couche 2
 * (`document_link.create` default `agent_allowed=true` — action interne non-sensitive,
 * broker JWT bypass naturel).
 *
 * UX Phase 1 (PO décisions Q3-Q4) :
 *   - File picker = `<select>` natif simple filtré sur tenant files scope parent
 *     (project_id ou submission_id selon scope) + `status=ready` only.
 *   - `link_role` input optional (1..80 chars).
 *   - `display_name` input optional (1..200 chars).
 *   - `classification` jsonb hors UI Phase 1 (peu utilisé broker — futur si retour PO).
 *   - Submit disabled tant que file_id non sélectionné.
 *   - Esc + backdrop close (sauf pendant submit).
 *   - Focus initial sur Cancel.
 *
 * Erreurs API (bilingues via `getApiErrorMessage`) :
 *   - 404 DOCUMENT_LINKS_FILE_NOT_FOUND        → bandeau red (file deleted entre listing et create)
 *   - 404 DOCUMENT_LINKS_ATTACHABLE_NOT_FOUND  → bandeau red (cross-tenant ou row supprimée race)
 *   - 422 DOCUMENT_LINKS_KIND_RESERVED         → bandeau red (defense in depth — rare)
 *   - 403 GATING_HUMAN_REQUIRED                → bandeau red (rare broker JWT)
 *   - 400 VALIDATION_ERROR                     → bandeau red
 */

import { useEffect, useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import type { AttachableKindActive } from "@kollectivia/shared";

import { ApiException } from "../../../lib/api";
import { getApiErrorMessage, normalizeLang } from "../../../lib/apiErrorMessage";
import { useCreateDocumentLink } from "../../../lib/queries/documentLinks";
import { useFiles } from "../../../lib/queries/files";
import { toast } from "../../../lib/toast";

export type CreateDocumentLinkDialogProps =
  | {
      readonly scope: "project";
      readonly projectId: string;
      readonly onClose: () => void;
    }
  | {
      readonly scope: "submission";
      readonly submissionId: string;
      readonly onClose: () => void;
    };

const LINK_ROLE_MAX = 80;
const DISPLAY_NAME_MAX = 200;

export function CreateDocumentLinkDialog(props: CreateDocumentLinkDialogProps) {
  const { t, i18n } = useTranslation();
  const lang = normalizeLang(i18n.resolvedLanguage);
  const titleId = useId();
  const descId = useId();
  const fileSelectId = useId();
  const linkRoleFieldId = useId();
  const displayNameFieldId = useId();
  const cancelBtnRef = useRef<HTMLButtonElement | null>(null);

  // List files scopés au parent + status=ready only.
  const filesQuery = useFiles(
    props.scope === "project"
      ? { project_id: props.projectId, status: "ready", limit: 200 }
      : { submission_id: props.submissionId, status: "ready", limit: 200 },
  );

  const attachableKind: AttachableKindActive = props.scope;
  const attachableId =
    props.scope === "project" ? props.projectId : props.submissionId;

  const create = useCreateDocumentLink();
  const { reset: resetMutation } = create;

  const [fileId, setFileId] = useState("");
  const [linkRole, setLinkRole] = useState("");
  const [displayName, setDisplayName] = useState("");

  useEffect(() => {
    resetMutation();
    setFileId("");
    setLinkRole("");
    setDisplayName("");
    queueMicrotask(() => cancelBtnRef.current?.focus());
  }, [resetMutation]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !create.isPending) {
        e.preventDefault();
        props.onClose();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [props, create.isPending]);

  const files = useMemo(() => filesQuery.data?.items ?? [], [filesQuery.data]);
  const filesEmpty = !filesQuery.isLoading && files.length === 0;

  const trimmedLinkRole = linkRole.trim();
  const trimmedDisplayName = displayName.trim();
  const linkRoleInvalid = trimmedLinkRole.length > LINK_ROLE_MAX;
  const displayNameInvalid = trimmedDisplayName.length > DISPLAY_NAME_MAX;
  const canSubmit =
    !!fileId &&
    !linkRoleInvalid &&
    !displayNameInvalid &&
    !create.isPending;

  const onConfirm = () => {
    if (!canSubmit) return;
    create.mutate(
      {
        input: {
          file_id: fileId,
          attachable_kind: attachableKind,
          attachable_id: attachableId,
          ...(trimmedLinkRole.length > 0 ? { link_role: trimmedLinkRole } : {}),
          ...(trimmedDisplayName.length > 0
            ? { display_name: trimmedDisplayName }
            : {}),
        },
      },
      {
        onSuccess: () => {
          toast.success(t("document_links.create_dialog.success"));
          props.onClose();
        },
      },
    );
  };

  const apiErrorMsg =
    create.error instanceof ApiException
      ? getApiErrorMessage(create.error, lang) || t("errors.generic")
      : create.isError
        ? t("errors.generic")
        : null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={descId}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
    >
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
        onClick={() => {
          if (!create.isPending) props.onClose();
        }}
      />
      <div className="relative z-10 w-full max-w-md rounded-xl bg-white shadow-xl ring-1 ring-slate-200 motion-safe:animate-fade-in">
        <header className="border-b border-slate-100 px-5 py-4">
          <h2 id={titleId} className="text-base font-semibold text-slate-900">
            {t("document_links.create_dialog.title")}
          </h2>
        </header>

        <div className="space-y-4 px-5 py-5">
          <p id={descId} className="text-sm text-slate-700">
            {t("document_links.create_dialog.body")}
          </p>

          <div>
            <label
              htmlFor={fileSelectId}
              className="mb-1.5 block text-xs font-medium text-slate-700"
            >
              {t("document_links.create_dialog.file_label")}{" "}
              <span aria-hidden="true" className="text-red-600">
                *
              </span>
            </label>
            <select
              id={fileSelectId}
              value={fileId}
              onChange={(e) => setFileId(e.target.value)}
              disabled={create.isPending || filesQuery.isLoading || filesEmpty}
              className="block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus-visible:border-brand-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 disabled:opacity-50"
            >
              <option value="">
                {filesQuery.isLoading
                  ? t("app.loading")
                  : filesEmpty
                    ? t("document_links.create_dialog.no_files")
                    : t("document_links.create_dialog.file_placeholder")}
              </option>
              {files.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.file_name}
                </option>
              ))}
            </select>
            {filesEmpty && (
              <p className="mt-1.5 text-xs text-amber-700">
                {t("document_links.create_dialog.no_files_hint")}
              </p>
            )}
          </div>

          <div>
            <label
              htmlFor={linkRoleFieldId}
              className="mb-1.5 block text-xs font-medium text-slate-700"
            >
              {t("document_links.create_dialog.link_role_label")}
            </label>
            <input
              id={linkRoleFieldId}
              type="text"
              maxLength={LINK_ROLE_MAX}
              value={linkRole}
              onChange={(e) => setLinkRole(e.target.value)}
              disabled={create.isPending}
              placeholder={t("document_links.create_dialog.link_role_placeholder")}
              className="block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus-visible:border-brand-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 disabled:opacity-50"
            />
            <p className="mt-1.5 text-xs text-slate-500">
              {t("document_links.create_dialog.link_role_hint")}
            </p>
          </div>

          <div>
            <label
              htmlFor={displayNameFieldId}
              className="mb-1.5 block text-xs font-medium text-slate-700"
            >
              {t("document_links.create_dialog.display_name_label")}
            </label>
            <input
              id={displayNameFieldId}
              type="text"
              maxLength={DISPLAY_NAME_MAX}
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              disabled={create.isPending}
              placeholder={t("document_links.create_dialog.display_name_placeholder")}
              className="block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus-visible:border-brand-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 disabled:opacity-50"
            />
            <p className="mt-1.5 text-xs text-slate-500">
              {t("document_links.create_dialog.display_name_hint")}
            </p>
          </div>

          {apiErrorMsg && (
            <div
              role="alert"
              className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
            >
              {apiErrorMsg}
            </div>
          )}
        </div>

        <footer className="flex items-center justify-end gap-2 border-t border-slate-100 px-5 py-4">
          <button
            ref={cancelBtnRef}
            type="button"
            onClick={props.onClose}
            disabled={create.isPending}
            className="rounded-md px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 disabled:opacity-50 touch-manipulation"
          >
            {t("common.cancel")}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={!canSubmit}
            aria-busy={create.isPending}
            className="inline-flex items-center gap-2 rounded-md bg-brand-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-brand-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-300 touch-manipulation"
          >
            {create.isPending && (
              <svg
                className="h-4 w-4 animate-spin motion-reduce:animate-none"
                viewBox="0 0 16 16"
                fill="none"
                aria-hidden="true"
              >
                <circle
                  cx="8"
                  cy="8"
                  r="6"
                  stroke="currentColor"
                  strokeWidth="2"
                  opacity="0.25"
                />
                <path
                  d="M14 8a6 6 0 0 1-6 6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            )}
            {create.isPending
              ? t("document_links.create_dialog.submitting")
              : t("document_links.create_dialog.submit")}
          </button>
        </footer>
      </div>
    </div>
  );
}
